/**
 * @module lib/commercial/keytar/Customer
 */
import { getProp, setProp } from "utils/utils";
import { roles } from "lib/identity/api";
import { ENTITLEMENTS, addEntitlement } from "lib/identity/entitlements";

/**
 * Create an object to encompass a user's a user's identity metadata.
 * @class Customer
 */
export default class Customer {
  /**
   * Creates an instance of Customer.
   * @param {Object} [identity={}] - collection of data
   * @param {Object} [identity.role] - that user has
   * @param {Number} [identity.role.id] - unique id
   * @param {String} [identity.role.name] - descriptive name
   * @param {String} [identity.ssvcs] - entire service string
   */
  constructor(identity = {}) {
    this.identity = identity;
  }

  /**
   * Get the customer's entire service string
   * @returns {String}
   */
  get subscribedServices() {
    return getProp(this.identity, "ssvcs", "");
  }

  /**
   * Set the customer's entire service string
   * @param {String} [services=""] - to assign
   */
  set subscribedServices(services = "") {
    setProp(this.identity, "ssvcs", services);
  }

  /**
   * Check if customer is entitled to a specific service.
   * @param {String} [service=""] - to check for
   * @returns {Boolean} true if entitled
   */
  hasService(service = "") {
    return this.subscribedServices.toLowerCase().indexOf(service.toLowerCase()) !== -1;
  }

  /**
   * Entitle the customer with a specific service.
   * @param {String} [service=""] - to add
   */
  addService(service = "") {
    if (!this.hasService(service)) {
      this.subscribedServices = addEntitlement(this.subscribedServices, service);
    }
  }

  /**
   * Get the customer's role id.
   * @example 0 = Anonymous (default), 1 = Registered, 2 = Subscribed
   * @readonly
   * @returns {Number}
   */
  get roleId() {
    return getProp(this.identity, "role.id", 0);
  }

  /**
   * Check if customer's role id is equal to parameter.
   * @param {Number} [id=0] - to check against
   * @returns {Boolean} true if match
   */
  hasRoleId(id = 0) {
    return this.roleId === id;
  }

  /**
   * Check whether the user is allowed to access content,
   * that's intended for "Unlimited" subscribers only.
   * @readonly
   * @returns {Boolean} true if allowed
   */
  get canAccessSubscriberContent() {
    return this.hasRoleId(roles.subscribed.id) || this.hasService(ENTITLEMENTS.ONDEMAND.value);
  }

  /**
   * Check whether the user is allowed to access content,
   * that's intended for subscribers or registered users. Not anonymous users.
   * @readonly
   * @returns {Boolean} true if allowed
   */
  get canAccessRegisteredContent() {
    return this.canAccessSubscriberContent || this.hasRoleId(roles.registered.id);
  }
}
